<template>
  <div class="closing-reasons-container">
    <ClosingReasonsFilters
      ref="filters"
      :reasonsData="closingReasons"
      @filter-change="onFilterChange"
    />
    <div class="closing-reasons-wrap">
      <div class="layout">
        <div class="total-section">
          <ClosingReasonsTotal :reasonsData="filteredClosingReasons" />
        </div>
        <div class="content">
          <ClosingReasonsDoughnutChart :reasonsData="filteredClosingReasons" />
        </div>
      </div>
      <div class="bar-section">
        <ClosingReasonsBarChart :reasonsData="filteredClosingReasons" />
      </div>
      <div class="table-section no-bg">
        <ClosingReasonsTable :reasonsData="filteredClosingReasons" />
      </div>
      <div class="table-section spacing-end">
        <ClosingReasonsSummaryTable :reasonsData="filteredClosingReasons" />
      </div>
    </div>
  </div>
</template>

<script>
import ClosingReasonsDoughnutChart from './components/ClosingReasonsDoughnutChart';
import ClosingReasonsTotal from './components/ClosingReasonsTotal';
import ClosingReasonsBarChart from './components/ClosingReasonsBarChart';
import ClosingReasonsTable from './components/ClosingReasonsTable';
import ClosingReasonsSummaryTable from './components/ClosingReasonsSummaryTable';
import ClosingReasonsFilters from './components/ClosingReasonsFilters';
import HistoryClosingReasonApi from '../../../../api/inbox/historyClosingReason';

export default {
  name: 'ClosingReasonsView',
  components: {
    ClosingReasonsFilters,
    ClosingReasonsDoughnutChart,
    ClosingReasonsTotal,
    ClosingReasonsBarChart,
    ClosingReasonsTable,
    ClosingReasonsSummaryTable,
  },
  data() {
    return {
      closingReasons: [],
      filters: {
        customDateRange: '',
        reasonIds: [],
        resolverIds: [],
        inboxNames: [],
      },
    };
  },
  computed: {
    filteredClosingReasons() {
      const [start, end] = this.filters.customDateRange || [];

      return this.closingReasons.filter(item => {
        const createdDate = new Date(item.created_at);
        const createdDateOnly = new Date(
          createdDate.getFullYear(),
          createdDate.getMonth(),
          createdDate.getDate()
        );

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        const matchReason =
          !this.filters.reasonIds.length ||
          this.filters.reasonIds.includes(item.closing_reason?.id);

        const matchResolver =
          !this.filters.resolverIds.length ||
          this.filters.resolverIds.includes(item.resolved_by?.id);

        const matchDate =
          !startDate ||
          !endDate ||
          (createdDateOnly >= startDate && createdDateOnly <= endDate);

        const matchInbox =
          !this.filters.inboxNames.length ||
          this.filters.inboxNames.includes(item.inbox_name);

        return matchReason && matchResolver && matchDate && matchInbox;
      });
    },
  },
  async mounted() {
    try {
      const dateRange = this.filters.customDateRange;
      const [start, end] = dateRange || [];

      const formatDate = date => new Date(date).toISOString().split('T')[0];

      const response = await HistoryClosingReasonApi.getAll({
        startDate: start ? formatDate(start) : null,
        endDate: end ? formatDate(end) : null,
      });

      this.closingReasons = response.data;

      this.$nextTick(() => {
        this.$refs.filters.emitFilters();
      });
    } catch (error) {
      console.error(this.$t('CLOSING_REASONS.ERRORS.FETCH'), error);
    }
  },
  methods: {
    async onFilterChange(newFilters) {
      this.filters = { ...this.filters, ...newFilters };

      try {
        this.closingReasons = [];

        const [start, end] = this.filters.customDateRange || [];

        const formatDate = date => new Date(date).toISOString().split('T')[0];

        const response = await HistoryClosingReasonApi.getAll({
          startDate: start ? formatDate(start) : null,
          endDate: end ? formatDate(end) : null,
        });

        this.closingReasons = response.data;
      } catch (error) {
        console.error(this.$t('CLOSING_REASONS.ERRORS.FETCH'), error);
      }
    },
  },
};
</script>

<style scoped>
.closing-reasons-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: auto;
}

.closing-reasons-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw;
  padding-bottom: 10vh;
  box-sizing: border-box;
  background: #f8fafc;
  min-width: 0;
  overflow: visible;
}

.layout {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  gap: 3rem;
  width: 100%;
  box-sizing: border-box;
}

.total-section {
  flex: 1 1 350px;
  min-width: 320px;
  max-width: 450px;
  box-sizing: border-box;
  flex-shrink: 1;
}

.content {
  flex: 1 1 450px;
  min-width: 350px;
  max-width: 650px;
  box-sizing: border-box;
  flex-shrink: 1;
}

.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
}

.table-section:last-of-type {
  margin-bottom: 10vh;
  padding-bottom: 170px;
  position: relative;
  background: white;
  box-shadow: none !important;
  border-radius: 0 !important;
}

.table-section:last-of-type::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  height: 170px;
  width: 100%;
  background: #f8fafc;
  z-index: 1;
  pointer-events: none;
  border-radius: 0;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.spacing-end {
  margin-bottom: 40px;
  background: transparent;
  padding: 0;
  box-shadow: none;
  border: none;
}

html,
body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}
</style>
